import type { ReactElement } from "react";
import {
  Map,
  Coordinate,
} from "src/domains/shared/react-openlayers";
import { configObject } from "src/awsconfig";
import { CAPTURE_CLOUDFRONT_URL } from "src/domains/shared/constants/constants";
import {
  GisGeojsonLayer,
  GisTmsLayer,
  LineLayerItem,
} from "src/domains/shared/api/gisLayer/gisLayer.model";
import { isLayerVisible2d } from "./utils";
import {
  resolveGisDxfJsonKey,
  resolveLineDxfJsonKey,
  resolveTmsLayerName,
} from "./paintOrder";
import { buildGeotiffTilePrefix } from "src/domains/shared/react-cesium/utils/tiling/tmsTileIndex";

export const ERROR_TILE_URL = "/mapicon/empty_tile.png";

// TMS 레이어 생성 (신규 2D 타입)
export const createTmsLayer = (
  layer: GisTmsLayer,
  zIndex: number,
  projectId: number,
  /**
   * OL 규약 불투명도(1=불투명). UI 투명도 → 불투명도 변환은 호출부가
   * `toTileOpacity` 로 끝냈다 — 여기서 다시 뒤집지 않는다(spec: 값 규약).
   */
  tileOpacity: number
): ReactElement | null => {
  if (!isLayerVisible2d(layer)) return null;
  if (!layer.tiles || !layer.matrix || !layer.zoom_level) return null;

  const layerName = resolveTmsLayerName(layer);
  if (!layerName) return null;

  return (
    <Map.TileLayer
      key={`tms-${layer.id}`}
      tileMatrix={layer.matrix}
      zIndex={zIndex}
      opacity={tileOpacity}
      errorTileUrl={ERROR_TILE_URL}
      url={`${buildGeotiffTilePrefix(
        configObject[CAPTURE_CLOUDFRONT_URL],
        projectId,
        layerName
      )}{z}/{x}/{y}.png`}
      maxZoom={Number(layer.zoom_level.max)}
      minZoom={Number(layer.zoom_level.min)}
    />
  );
};

// GeoJSON 레이어 생성 (신규 2D 타입)
export const createGeoJsonLayer = (
  layer: GisGeojsonLayer,
  zIndex: number,
  dxfData: Record<string, any>,
  projectionCode: string
): ReactElement | null => {
  if (!isLayerVisible2d(layer)) return null;

  const layerName = resolveGisDxfJsonKey(layer);
  if (!layerName || !dxfData[layerName]) return null;

  return (
    <Map.GeoJsonLayer
      key={`geojson-${layer.id}`}
      zIndex={zIndex}
      color={layer.color}
      geoJson={dxfData[layerName] as Record<string, any>}
      projectionCode={projectionCode as Coordinate}
    />
  );
};

// 선형 레이어 생성 (신규 2D 타입)
export const createLinearLayer = (
  layer: LineLayerItem,
  zIndex: number,
  lineDxfData: Record<string, any>,
  projectionCode: string
): ReactElement | null => {
  if (!isLayerVisible2d(layer)) return null;

  const layerName = resolveLineDxfJsonKey(layer);
  if (!layerName || !lineDxfData[layerName]) return null;

  return (
    <Map.GeoJsonLayer
      key={`linear-${layer.id ?? layerName}`}
      zIndex={zIndex}
      color={layer.color}
      geoJson={lineDxfData[layerName] as Record<string, any>}
      projectionCode={projectionCode as Coordinate}
    />
  );
};
